import { getAccessToken, getRefreshToken, getItem } from 'app/services/api';
import { actions } from 'auth';
import useTokenRefresher, { isTokenExpired } from 'auth/hooks/useTokenRefresh';
import {
  isAuthenticated as isAuthenticatedSelector,
  isOtpLoading as isOtpLoadingSelector,
  otpExchangeError as otpExchangeErrorSelector,
  signOutSuccess as signOutSuccessSelector,
  signOutViaOtp as signOutViaOtpSelector,
} from 'auth/reducers';
import { actions as sessionActions } from 'session';
import { getAuthServiceUrl } from 'auth/utils/getAuthServiceUrl';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import {
  UO_loginRoute,
  signInRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  onboardingRoute,
} from './routes';

const PUBLIC_ROUTES = [signInRoute, forgotPasswordRoute, resetPasswordRoute];

const isPublicRoute = (pathname) =>
  PUBLIC_ROUTES.some((route) => pathname.startsWith(route)) ||
  pathname.startsWith(onboardingRoute);

const readStoredTokens = () => ({
  accessToken: getAccessToken(),
  refreshToken: getRefreshToken(),
  legacyToken: getItem('signInToken'),
  identificationToken: getItem('identificationToken'),
});

const hasLegacySession = ({ legacyToken, identificationToken }) =>
  Boolean(legacyToken || identificationToken);

const hasOtpSession = ({ accessToken, refreshToken }) =>
  Boolean(accessToken || refreshToken);

const goToLogin = () => {
  const authServiceUrl = getAuthServiceUrl();
  window.location = `${authServiceUrl}${UO_loginRoute}`;
};

const AuthHandler = ({ children }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { pathname, search } = location;

  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const isOtpLoading = useSelector(isOtpLoadingSelector);
  const isSignOutSuccess = useSelector(signOutSuccessSelector);
  const isSignOutViaOtp = useSelector(signOutViaOtpSelector);
  const otpExchangeError = useSelector(otpExchangeErrorSelector);

  const visitingPublicRoute = isPublicRoute(pathname);

  useTokenRefresher();

  useEffect(() => {
    if (!isSignOutSuccess) return;

    if (isSignOutViaOtp) {
      goToLogin();
      return;
    }

    if (pathname !== signInRoute) {
      history.replace(signInRoute);
    }
  }, [history, isSignOutSuccess, isSignOutViaOtp, pathname]);

  useEffect(() => {
    if (otpExchangeError) {
      goToLogin();
    }
  }, [otpExchangeError]);

  useEffect(() => {
    const tokens = readStoredTokens();

    if (hasLegacySession(tokens) && hasOtpSession(tokens)) {
      dispatch(sessionActions.sessionClear());
      if (pathname !== signInRoute) {
        history.replace(signInRoute);
      }
    }
  }, [dispatch, history, pathname]);

  useEffect(() => {
    const { otp: otpToken } = queryString.parse(search);

    if (otpToken) {
      if (!isAuthenticated && !isOtpLoading && !otpExchangeError) {
        dispatch(actions.otpExchangeRequest(otpToken));
      }
      return;
    }

    if (
      isAuthenticated ||
      isOtpLoading ||
      visitingPublicRoute ||
      otpExchangeError
    )
      return;

    const tokens = readStoredTokens();
    const { refreshToken } = tokens;

    if (hasLegacySession(tokens)) {
      return;
    }

    if (!refreshToken) {
      if (pathname !== signInRoute) {
        history.replace(signInRoute);
      }
      return;
    }

    if (isTokenExpired(refreshToken)) {
      goToLogin();
      return;
    }

    dispatch(actions.tokenRefreshRequest({ refreshToken }));
  }, [
    dispatch,
    history,
    isAuthenticated,
    isOtpLoading,
    visitingPublicRoute,
    otpExchangeError,
    pathname,
    search,
  ]);

  const shouldRenderChildren = isAuthenticated || visitingPublicRoute;

  return shouldRenderChildren ? <>{children}</> : null;
};

AuthHandler.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthHandler;
